import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import { CiSearch } from "react-icons/ci";
import { useQuery } from "@tanstack/react-query";
import { getUsers } from "../../../api/admin/dashboard";
import Pagination from "../../../components/common/Pagination";
import { useTranslation } from "../../../hooks/useTranslation";

const UserManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const limit = 10;

  // Debounce search input (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["users", page, debouncedSearch],
    queryFn: () => getUsers({ page, limit, search: debouncedSearch }),
    keepPreviousData: true,
  });

  const users = data?.data?.user || [];
  const pagination = data?.data?.pagination || {};

  return (
    <>
      <div className="flex gap-[10px] items-center">
        <div
          onClick={() => navigate(-1)}
          className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]"
        >
          <IoIosArrowRoundBack size={24} className="text-[#fff]" />
        </div>
        <h2 className="text-[22px] font-medium">
          {t("userManagement").title}
        </h2>
      </div>

      <div className="mt-[31px]">
        <div className="mt-6 bg-white border rounded-[10px] overflow-hidden p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold">
              {t("userManagement").userList}
            </h3>
            <div className="relative">
              <CiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg" />
              <input
                type="text"
                placeholder={t("userManagement").searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 w-[280px]"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#FFFAF2]">
                <tr>
                  <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                    {t("userManagement").tableHeaders.name}
                  </th>
                  <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                    {t("userManagement").tableHeaders.email}
                  </th>
                  <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                    {t("userManagement").tableHeaders.phone}
                  </th>
                  <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                    {t("userManagement").tableHeaders.role}
                  </th>
                  <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                    {t("userManagement").tableHeaders.status}
                  </th>
                  <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                    {t("userManagement").tableHeaders.createdAt}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="py-5 text-center text-gray-500">
                      {t("userManagement").loading || "Loading..."}
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan="6" className="py-5 text-center text-red-500">
                      {error?.message || t("userManagement").error}
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-5 text-center text-gray-500">
                      {t("userManagement").noUsers}
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user._id} className="border-t">
                      <td className="py-3 px-4">{user.name || "N/A"}</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {user.email || "N/A"}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {user.phoneNumber || "N/A"}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="capitalize">
                          {user.role || "N/A"}
                        </span>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div
                          className={`p-[2px_8px_2px_8px] rounded-full w-fit text-center text-sm text-white ${
                            user.status === "active"
                              ? "bg-[#4CAF50]"
                              : user.status === "inactive"
                              ? "bg-[#FF9D00]"
                              : "bg-[#FF0000]"
                          }`}
                        >
                          {user.status
                            ? user.status.charAt(0).toUpperCase() +
                              user.status.slice(1)
                            : "N/A"}
                        </div>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString()
                          : "N/A"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {users.length > 0 && pagination.totalPages > 0 && (
            <Pagination
              page={pagination.page || page}
              totalPages={pagination.totalPages || 1}
              setPage={setPage}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default UserManagement;

